import React, { useState, useEffect, useCallback } from "react";
import ApiService from "../services/ApiService";
import { toast } from "react-toastify";
import { Timer } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Swal from 'sweetalert2';
import Modal from "../components/Modal";
import ReviewModal from "../components/ReviewModal";
import TestInfo from "../components/TestInfo";
import Calculator from "../components/Calculator";
import Navigator from "../components/Navigator";
import PageLoader from "../components/PageLoader";
import { Table, Container } from "react-bootstrap";
import { useParams } from 'react-router-dom';
import ReviewScreen from "../components/ReviewScreen";
import SummaryScreen from "../components/SummaryScreen";

const Test = () => {
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState([]);
  const [exam_type, setExamType] = useState(null);
  const [test, setTest] = useState(null);
  const [subset, setSubset] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [qIndex, setQuestionIndex] = useState(0);
  const [review, setReview] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [reviewModal, setReviewModal] = useState(false);
  const [promptShown, setPromptShown] = useState(false);
  const [currentPage, setCurrentPage] = useState("info");
  const [isCalculatorOpen, setCalculatorOpen] = useState(false);
  const [isNavigatorOpen, setNavigatorOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answers, setAnswers] = useState({});
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Memoized API calls
  const getTestDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ApiService.get("/getTestDetails?test_id=" + id);
      const data = response.data;

      if (data?.status === true) {
        setTest(data.data);
      } else {
        toast.error(data?.message || "Failed to fetch packages.");
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const checkSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ApiService.post("/checkSubscription");
      const data = response.data;
      if (data?.status !== true) {
        toast.error(data?.message);
        navigate('/packages');
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const getSubsetQuestions = useCallback(async () => {
    if (!subset?.id || !attempt?.id) return;
    
    try {
      setLoading(true);
      const response = await ApiService.get(
        `/getSubsetQuestions?subset_id=${subset.id}&attempt_id=${attempt.id}`
      );
      const data = response.data;

      if (data?.status === true) {
        setQuestions(data.data);
        setQuestionIndex(0);
        setCurrentPage("question");
      } else {
        toast.error(data?.message || "Failed to fetch data.");
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  }, [subset, attempt]);

  const viewQuestion = useCallback(async () => {
    if (!questions || review === "final" || !questions[qIndex]?.id) return;

    const question = questions[qIndex];
    const question_type = question?.sub_questions?.length > 0 ? "sub_type" : null;
    
    try {
      await ApiService.request({
        method: "POST",
        url: `viewQuestion`,
        params: {
          question_id: question.id,
          attempt_id: attempt?.id,
          question_type: question_type
        },
      });
    } catch (error) {
      console.error("Error viewing question:", error);
    }
  }, [questions, qIndex, attempt, review]);

  const flagQuestion = useCallback(async () => {
    if (!questions || !questions[qIndex]?.id || !attempt?.id) return;

    try {
      const response = await ApiService.request({
        method: "POST",
        url: `flagQuestion`,
        params: {
          question_id: questions[qIndex].id,
          attempt_id: attempt.id
        },
      });
      const data = response.data;

      if (data?.status === true) {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex] = {
          ...updatedQuestions[qIndex],
          is_flagged: updatedQuestions[qIndex].is_flagged === 0 ? 1 : 0,
        };
        setQuestions(updatedQuestions);
      } else {
        toast.error(data?.message || "Failed to flag question.");
      }
    } catch (error) {
      console.error("Error flagging question:", error);
    }
  }, [questions, qIndex, attempt]);

  const startTest = useCallback(async (exam_type = null) => {
    if (!test?.id) return;

    try {
      const response = await ApiService.request({
        method: "POST",
        url: `startTest`,
        data: {
          test_id: test.id,
          exam_type: exam_type
        },
      });
      const data = response.data;

      if (data?.status === true) {
        setAttempt(data.data);
        localStorage.setItem('attempt', JSON.stringify(data.data));
      } else {
        toast.error(data?.message || "Failed to start test.");
      }
    } catch (error) {
      console.error("Error starting test:", error);
    }
  }, [test]);

  //Next Function
  const handleNext = useCallback(() => {
    switch (currentPage) {
      case "info":
        if (!promptShown) {
          setModalOpen(true);
        }
        break;
      case "subsets":
        setSubset(test.sections[0]);
        setCurrentPage("subset");
        setModalOpen(false);
        break;
      case "subset":
        getSubsetQuestions();
        break;
      case "question":
        if (review === "flagged") {
          const nextFlaggedIndex = questions.findIndex((q, i) => i > qIndex && q.is_flagged === 1);
          if (nextFlaggedIndex >= 0) {
            setQuestionIndex(nextFlaggedIndex);
          } else {
            setCurrentPage("review");
          }
        } else if (qIndex + 1 < questions?.length) {
          setQuestionIndex(qIndex + 1);
        } else {
          setCurrentPage("review");
        }
        break;
      case "summary":
        // No action needed
        break;
      default:
        break;
    }
  }, [currentPage, promptShown, test, getSubsetQuestions, questions, qIndex, review]);

  //Previous Function
  const handlePrevious = useCallback(() => {
    if (currentPage === "question" && qIndex > 0) {
      setQuestionIndex(qIndex - 1);
    }
  }, [currentPage, qIndex]);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event) => {
      switch (event.key.toLowerCase()) {
        case 'n':
          handleNext();
          event.preventDefault();
          break;
        case 'p':
          handlePrevious();
          event.preventDefault();
          break;
        case 'c':
          setCalculatorOpen(true);
          event.preventDefault();
          break;
        case 'f':
          flagQuestion();
          event.preventDefault();
          break;
        case 'v':
          setNavigatorOpen(true);
          event.preventDefault();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrevious, flagQuestion]);

  // Initial data loading
  useEffect(() => {
    getTestDetails();
    checkSubscription();
  }, [getTestDetails, checkSubscription]);

  // View question when question index changes
  useEffect(() => {
    if (currentPage === "question") {
      viewQuestion();
    }
  }, [qIndex, currentPage, viewQuestion]);

  // Handle URL parameters for review/continue test
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    // Report Review Case
    if (queryParams.get("review") === "final") {
      const attempt={
        "id":queryParams.get("attempt"),
      };
      setAttempt(attempt);
      setReview(queryParams.get("review"));
      setCurrentPage("summary");
      queryParams.delete("review");
      navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
    }
    // Test Continue Case
    if (test && Object.keys(test).length > 0) { 

      if (queryParams.has("attempt") && queryParams.has("subset_id")) {
        const attempt={
          "id":queryParams.get("attempt"),
        };
        setAttempt(attempt);
        let new_subset;
        if (queryParams.has("subset_id") && queryParams.get("subset_id")!=='') {
            const subsetId=queryParams.get("subset_id");
            console.log(test.sections);
            console.log(subsetId);

            const subsetIndex = test.sections.findIndex(sub => sub.id === Number(subsetId));
            new_subset=test.sections[subsetIndex];
        }else{
            new_subset=test.sections[0];
        }

        setSubset(new_subset);
        getSubsetQuestions();

    
        if (subset && questions) {
          const questionId=queryParams.get("question_id");
          if (questionId!=='') {
            const qIndex = questions.findIndex(quest => quest.id === Number(questionId) );
            setTimeout(() => {
              setQuestionIndex(qIndex);
              console.log('here');
              // **Remove subset_id and question_id from URL**
              queryParams.delete("subset_id");
              queryParams.delete("question_id");
              navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
            }, 2000);
           
          }
        }


      }
    }

  }, [ location.search, test,questions]);


  // Timer effect
  useEffect(() => {
    let timer;
    
    const setTimer = (seconds) => {
      setTimeLeft(seconds);
      if (exam_type === "time") {
        timer = window.setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 0) {
              handleNext();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    };

    if (currentPage === "subsets") {
      setTimer(2 * 60); // 2 mins for all subsets info
    } else if (currentPage === "subset" && subset) {
      setTimer(subset.instruction_mins * 60);
    } else if (currentPage === "question" && subset) {
      setTimer(subset.section_mins * 60);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [currentPage, subset, exam_type, handleNext]);

  // Prevent accidental tab/window close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);



  const selectAnswer = useCallback(async (optionId) => {
    if (review === "final" || !questions?.[qIndex]?.id) return;

    const updatedQuestions = questions.map((q, i) =>
      i === qIndex ? { ...q, answer_id: optionId } : q
    );
    setQuestions(updatedQuestions);

    try {
      await ApiService.request({
        method: "POST",
        url: `updateAnswer`,
        data: {
          attempt_id: attempt.id,
          question_id: questions[qIndex].id,
          answer_id: optionId
        },
      });
    } catch (error) {
      console.error("Error updating answer:", error);
    }
  }, [questions, qIndex, attempt, review]);

  const updateReview = useCallback((reviewType) => {
    if (!questions) return;

    let newIndex = 0;
    switch (reviewType) {
      case "flagged":
        newIndex = questions.findIndex(q => q.is_flagged === 1);
        if (newIndex < 0) {
          toast.error('No flagged question found');
          return;
        }
        break;
      case "incomplete":
        newIndex = questions.findIndex(q => !q.answer_id);
        if (newIndex < 0) {
          toast.error('No incomplete question found');
          return;
        }
        break;
      case "all":
        newIndex = 0;
        break;
      default:
        return;
    }

    setReview(reviewType);
    setQuestionIndex(newIndex);
    setCurrentPage("question");
  }, [questions]);

  const endReview = useCallback(async () => {
    const result = await Swal.fire({
      title: 'End Review Confirmation',
      text: 'Please confirm that you want to end this review. If you click Yes, you will NOT be able to return to this review! Are you sure you want to end this review?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes!',
      cancelButtonColor: '#9E9B9B',
      cancelButtonText: 'no',
    });

    if (result.isConfirmed) {
      const subsetIndex = test.sections.findIndex(sub => sub.id === subset.id);
      const new_subset = test.sections[subsetIndex + 1];
      
      if (new_subset) {
        setSubset(new_subset);
        if (review !== 'final') {
          setReview(null);
        }
        setCurrentPage("subset");
        setQuestionIndex(0);
      } else {
        setCurrentPage("summary");
      }
    }
  }, [test, subset, review]);

  const finalReviewSubset = useCallback((subsetId) => {
    const subsetIndex = test.sections.findIndex(sub => sub.id === subsetId);
    const new_subset = test.sections[subsetIndex];
    if (new_subset) {
      setSubset(new_subset);
      setReview('final');
      setCurrentPage("subset");
    }
  }, [test]);

  const handleDragStart = (e, answer) => {
    e.dataTransfer.setData("answer", answer);
  };

  const handleDrop = useCallback(async (e, questionId) => {
    e.preventDefault();
    const answer = e.dataTransfer.getData("answer");
    
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    try {
      await ApiService.request({
        method: "POST",
        url: `updateAnswer`,
        data: {
          attempt_id: attempt.id,
          question_id: questionId,
          answer: answer === 'yes' ? 1 : 0,
          type: 'sub_type'
        },
      });
    } catch (error) {
      console.error("Failed to update answer:", error);
    }
  }, [attempt]);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const toggleCalculator = () => setCalculatorOpen(!isCalculatorOpen);
  const toggleNavigator = () => setNavigatorOpen(!isNavigatorOpen);

  const handleConfirm = (type) => {
    setExamType(type);
    setModalOpen(false);
    setPromptShown(true);
    setCurrentPage("subsets");
    startTest(type);
  };

  const handleClose = () => {
    setModalOpen(false);
    setReviewModal(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const renderPageContent = () => {
    switch (currentPage) {
      case "info":
        return <TestInfo />;
      case "subsets":
        return (
          <Container className="mt-5 mb-10">
            <h4>You have 2 minutes to read these instructions.</h4>
            <p>This exam consists of five subtests:</p>
            <Table bordered className="text-center">
              <thead>
                <tr>
                  <th rowSpan="2" className="align-middle">Subtest</th>
                  <th rowSpan="2" className="align-middle">Number of Questions</th>
                  <th colSpan="2">Subtest Time</th>
                </tr>
                <tr>
                  <th colSpan="2">UCAT or UCAT_ANZ</th>
                </tr>
              </thead>
              <tbody>
                {test?.sections?.map((subset, index) => (
                  <tr key={index}>
                    <td>{subset.title}</td>
                    <td>{subset.questions_count}</td>
                    <td>{subset.section_mins} mins</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="text-center">
              Please click the <strong>Next (N)</strong> button to proceed
              before the time on this screen expires.
            </p>
          </Container>
        );
      case "subset":
        return (
          <Container className="mt-5 mb-10">
            <h6>The time allocated for reading these instructions is in the top right of the screen. When the time expires the subtest will begin..</h6>
            <h4>{subset?.title} Instructions:</h4>
            <p>{subset?.description}</p>
            <Table bordered className="text-center w-50 mx-auto table-striped mt-5">
              <thead>
                <tr>
                  <th rowSpan="2" className="align-middle">
                    Number of Questions
                  </th>
                  <th colSpan="2">Subtest Time</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{subset?.questions_count}</td>
                  <td>{subset?.section_mins} mins</td>
                </tr>
              </tbody>
            </Table>
            <p className="text-center">
              Please click the <strong>Next (N)</strong> button to proceed
              before the time on this screen expires.
            </p>
          </Container>
        );
      case "question":
        return (
          <>
            <div className="container-fluid mt-4 mb-10">
              <div className="row">
                <div className="col-sm-12 col-md-7">
                  <div className="card border-0">
                    <div className="card-body">
                      <div className="mb-4">
                        <h4>{questions?.[qIndex]?.text}</h4>
                        {questions?.[qIndex]?.image && (
                          <img src={questions[qIndex].image} alt="question" className="img-fluid w-75" />
                        )}
                      </div>
                      {questions?.[qIndex]?.type === 'sub_type' ? (
                        <div className="row">
                          <div className="col-md-8">
                            {questions?.[qIndex]?.sub_questions?.map((subQ, index) => {
                              // Determine which answer to display
                              let displayAnswer = '';
                              if (review === "final") {
                                // In review mode, show the correct answer from API
                                displayAnswer = subQ.sub_type_answer === "1" ? "yes" : "no";
                              } else {
                                // In test mode, show user's dragged answer
                                displayAnswer = answers[subQ.id] || '';
                              }
                              return (
                                <div key={subQ.id} className="row align-items-center mb-3 p-3 border rounded">
                                  <div className="col-md-8">
                                    <div className="d-flex">
                                      <span className="fw-bold me-2">{index + 1}.</span>
                                      <p className="mb-0">{subQ.text}</p>
                                    </div>
                                    {subQ.image && (
                                      <img src={subQ.image} alt={`sub-question-${index}`} className="img-fluid w-50 mt-2" />
                                    )}
                                  </div>
                                  <div className="col-md-4">
                                    <div 
                                      className={`hetre border p-3 text-center h-100 ${
                                        review === "final" ? "bg-light" : "bg-light"
                                      }`}
                                      onDrop={review !== "final" ? (e) => handleDrop(e, subQ.id) : undefined}
                                      onDragOver={review !== "final" ? handleDragOver : undefined}
                                    >
                                      {displayAnswer ? (
                                        <div className={`p-2 ${displayAnswer === 'yes' ? 'bg-dark text-white' : 'bg-dark text-white'}`}>
                                          {displayAnswer.toUpperCase()}
                                        </div>
                                      ) : (
                                        <span className="text-muted bg-gray-800 okkk">Drop answer here</span>
                                      )}
                                      {/* Show correct answer in review mode */}
                                      {/* {review === "final" && (
                                        <div className="mt-2 p-1 bg-success text-white">
                                          Correct: {subQ.sub_type_answer === "1" ? "YES" : "NO"}
                                        </div>
                                      )} */}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="col-md-4">
                            <div className="sticky-center mt-4" style={{ top: '80px' }}>
                              <div className="card">
                                <div className="card-body">
                                  <div
                                    className="border p-3 mb-3 text-black text-center"
                                    style={{ backgroundColor: '#90EE90' }} // Light Green
                                    draggable={review !== "final"}
                                    onDragStart={review !== "final" ? (e) => handleDragStart(e, 'yes') : undefined}
                                  >
                                    YES
                                  </div>
                                  <div
                                    className="border p-3 text-black text-center simple"
                                    style={{ backgroundColor: '#FF7F7F' }} // Light Red
                                    draggable={review !== "final"}
                                    onDragStart={review !== "final" ? (e) => handleDragStart(e, 'no') : undefined}
                                  >
                                    NO
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        null
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-sm-12 col-md-4">
                  <div className="card border-0">
                    <div className="card-body">
                      <h5 className="card-title">
                        Question {qIndex + 1}: {questions?.[qIndex]?.text}
                        {questions?.[qIndex]?.image && 
                          <img src={questions[qIndex].image} alt="question" className="w-75" /> 
                        }
                      </h5>
                      <form>
                        {questions?.[qIndex]?.options?.map((option, index) => (
                          <div className="form-check my-4" key={index}>
                            <input
                              className="form-check-input"
                              type="radio"
                              checked={option.id === questions[qIndex].answer_id}
                              name="answer"
                              onChange={() => selectAnswer(option.id)}
                              id={`option${index}`}
                            />
                            <label className="form-check-label" htmlFor={`option${index}`}>
                              {review === "final" ? (
                                option.id == questions[qIndex].answer_id ? (
                                  <span className={option.is_correct ? "text-success fw-bold" : "text-danger fw-bold"}>
                                    {option.text} <i className={option.is_correct ? "bi bi-check-circle-fill okk" : "bi bi-x-circle-fill"}></i>
                                  </span> 
                                ) : (
                                  <span className={option.is_correct ? "text-success fw-bold" : ""}>
                                    {option.text} <i className={option.is_correct ? "bi bi-check-circle-fill hhh" : ""}></i>
                                  </span> 
                                )
                              ) : (
                                option.text
                              )}
                              {option.image && 
                                <img src={option.image} alt="question" className="w-75" /> 
                              }
                            </label>
                          </div>
                        ))}
                        
                        {questions?.[qIndex] && review === "final" && (
                          <p className="text-primary bg-light border p-2">
                            <b className="text-dark">Answer Details:</b> <br />
                            {questions[qIndex].answer_details}
                            <div className="image">
                              {questions[qIndex].answer_image && 
                                <img src={questions[qIndex].answer_image} alt="answer" className="w-75" /> 
                              }
                            </div>
                          </p>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case "review":
        return (
          <ReviewScreen 
            subset={subset} 
            attempt_id={attempt?.id} 
            setQuestionIndex={setQuestionIndex} 
          />
        );
      case "summary":
        return (
          <SummaryScreen 
            attempt_id={attempt?.id} 
            onReviewClick={finalReviewSubset} 
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="d-flex flex-column min-vh-100 position-relative">
      <header className="header-top text-white text-left px-3 py-2">
        <h3 className="mb-0">{test?.title}
          {currentPage === "review" && 
            <h5 className="mt-3 cursor-pointer" onClick={() => setReviewModal(true)}>
              <i className="bi bi-info-circle-fill"></i> Instructions
            </h5>
          }
        </h3>
        {review !== "final" && (
          <div className="flex items-center gap-2">
            {(["subsets", "subset", "question"].includes(currentPage) && exam_type === "time") && (
              <div>
                <Timer className="w-5 h-5" />
                <span className="font-mono">{formatTime(timeLeft)}</span>
              </div>
            )}
            {currentPage === "question" && (
              <p className="m-0">
                <i className="bi bi-list"></i> {qIndex + 1} of {questions?.length || 0}
              </p>
            )}
          </div>
        )}
      </header>
      
      {currentPage === "question" && (
        <div className="header-ii w-100">
          <div className="bg-gray-700 text-white p-2 header-ii">
            <span onClick={toggleCalculator} className="text-white">
              <i className="bi bi-calculator"></i>
              Calculator
            </span>
          </div>
          <div className="bg-gray-700 text-white p-2">
            {questions?.[qIndex] && review !== "final" && 
              <span className="text-white" onClick={flagQuestion}>
                <i className={questions[qIndex].is_flagged === 1 ? "bi bi-flag-fill text-danger" : "bi bi-flag"}></i>
                Flag for review
              </span>
            }
          </div>
        </div>
      )}

      <div className="flex-grow-1">{renderPageContent()}</div>

      <footer className="footer-test text-white py-3 mt-4">
        {currentPage === "summary" && (
          <Link to="/dashboard?tab=myTest" className="btn btn-base mx-3">
            <i className="bi bi-box-arrow-right end-exam-icon"></i> End {review !== 'final' ? 'Exam' : 'Report'}
          </Link>
        )}
        
        {currentPage === "question" && (
          <div className="w-100">
            {review === "final" && (
              <button 
                onClick={() => setCurrentPage("summary")}
                className="btn btn-base mx-3 float-start"
              >
                <i className="bi bi-box-arrow-right end-exam-icon"></i> Summary  
              </button>
            )}

            {review && review !== 'final' &&
              <button 
                className="btn btn-light float-start" 
                onClick={() => setCurrentPage("review")}
              >
                <i className="bi bi-box-arrow-left"></i> Review Screen 
              </button>
            }
            
            <button 
              onClick={toggleNavigator} 
              className="btn btn-light float-end"
            >
              <i className="bi bi-compass"></i>
              Navigator
            </button>
            {qIndex > 0 &&
              <button 
                className="btn btn-light float-end" 
                onClick={handlePrevious}
              >
                <i className="bi bi-arrow-left"></i> Previous
              </button>
            }
          </div>
        )}
        
        {!["review", "summary"].includes(currentPage) && (
          <button 
            className="btn btn-light float-end" 
            style={{width: '100px'}} 
            onClick={handleNext}
          >
            Next <i className="bi bi-arrow-right"></i>
          </button>
        )}
        
        {currentPage === "review" && (
          <div className="w-100">
            {review === "final" ? (
              <button 
                onClick={() => setCurrentPage("summary")}
                className="btn btn-base mx-3 float-start"
              >
                <i className="bi bi-box-arrow-right end-exam-icon"></i> Summary  
              </button>
            ) : (
              <div>
                <button 
                  className="btn btn-light float-start" 
                  onClick={endReview}
                >
                  <i className="bi bi-box-arrow-left"></i> End Review 
                </button>

                <button 
                  className="btn btn-light float-end" 
                  onClick={() => updateReview('flagged')}
                >
                  <i className="bi bi-flag-fill"></i> Review Flagged
                </button>
                <button 
                  className="btn btn-light float-end" 
                  onClick={() => updateReview('incomplete')}
                >
                  <i className="bi bi-x-circle-fill"></i> Review Incomplete
                </button>
                <button 
                  className="btn btn-light float-end" 
                  onClick={() => updateReview('all')}
                >
                  <i className="bi bi-bookmarks-fill"></i> Review All
                </button>
              </div>
            )}
          </div>
        )}
      </footer>

      {isCalculatorOpen && currentPage === "question" && (
        <Calculator toggleCalculator={toggleCalculator} />
      )}

      {isNavigatorOpen && currentPage === "question" && (
        <Navigator 
          toggleNavigator={toggleNavigator} 
          subset_id={subset?.id} 
          attempt_id={attempt?.id} 
          setQuestionIndex={setQuestionIndex}
        />
      )}

      <Modal
        isOpen={modalOpen}
        message="Are you sure you want to proceed to the next page?"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      
      <ReviewModal
        isOpen={reviewModal}
        onClose={handleClose}
      />
    </div>
  );
};

export default Test;